import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Podcasts, Categories, Tags } from '../lib/supabase-types';
import slugify from 'slugify';

export type PodcastWithRelations = Podcasts & {
  category: Categories | null;
  tags: Tags[];
};

function usePodcasts() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [podcasts, setPodcasts] = useState<PodcastWithRelations[]>([]);

  const fetchPodcasts = async () => {
    try {
      setLoading(true);
      console.log('Fetching podcasts...');

      // First get all podcasts
      const { data: podcasts, error: podcastsError } = await supabase
        .from('podcasts')
        .select('*')
        .order('created_at', { ascending: false });

      if (podcastsError) {
        console.error('Error fetching podcasts:', podcastsError);
        throw new Error('Erro ao carregar podcasts do servidor');
      }
      
      if (!podcasts) {
        console.log('No podcasts found');
        return [];
      }

      // Get all categories
      const { data: categories, error: categoriesError } = await supabase
        .from('categories')
        .select('*');

      if (categoriesError) {
        console.error('Error fetching categories:', categoriesError);
        throw new Error('Erro ao carregar categorias');
      }

      // Get all podcast tags
      const { data: podcastTags, error: tagsError } = await supabase
        .from('podcast_tags')
        .select('podcast_id, tags(*)');

      if (tagsError) {
        console.error('Error fetching tags:', tagsError);
        throw new Error('Erro ao carregar tags');
      }

      // Map the data together
      const podcastsWithRelations = podcasts?.map(podcast => {
        const category = categories?.find(c => c.id === podcast.category_id) || null;
        const tags = podcastTags
          ?.filter(pt => pt.podcast_id === podcast.id)
          .map(pt => pt.tags)
          .filter(Boolean) || [];

        return {
          ...podcast,
          category,
          tags
        };
      });

      console.log('Podcasts loaded successfully:', podcastsWithRelations?.length || 0);
      setPodcasts(podcastsWithRelations || []);
      return podcastsWithRelations as PodcastWithRelations[];
    } catch (err) {
      console.error('Error fetching podcasts:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar podcasts');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const createPodcast = async (podcast: Omit<Podcasts, 'id' | 'created_at' | 'updated_at' | 'plays' | 'slug'>) => {
    setLoading(true);
    setError(null);

    try {
      if (!podcast.title) {
        throw new Error('Title is required');
      }

      if (!podcast.category_id) {
        throw new Error('Category is required');
      }

      const slug = slugify(podcast.title, { lower: true, strict: true });

      // Create podcast data object with only the fields we want to insert
      const podcastData = {
        title: podcast.title,
        description: podcast.description,
        category_id: podcast.category_id,
        audio_url: podcast.audio_url,
        image_url: podcast.image_url,
        author: podcast.author,
        duration: podcast.duration,
        published_at: podcast.published_at || new Date().toISOString(),
        slug,
        plays: 0
      };

      // Insert the podcast
      const { data: newPodcast, error: insertError } = await supabase
        .from('podcasts')
        .insert([podcastData])
        .select()
        .single();

      if (insertError) throw insertError;

      // Get the category
      const { data: category, error: categoryError } = await supabase
        .from('categories')
        .select()
        .eq('id', podcast.category_id)
        .single();

      if (categoryError) throw categoryError;

      return {
        ...newPodcast,
        category,
        tags: []
      } as PodcastWithRelations;
    } catch (err: unknown) {
      console.error('Error creating podcast:', err);
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePodcast = async (id: string, podcast: Partial<PodcastWithRelations>) => {
    try {
      setLoading(true);
      setError(null);

      if (podcast.title) {
        podcast.slug = slugify(podcast.title, { lower: true, strict: true });
      }

      // Extrair e normalizar os tags do objeto de atualizações
      const { tags, category, ...podcastUpdates } = podcast;

      // Atualizar o podcast principal
      const { data: updatedPodcast, error: updateError } = await supabase
        .from('podcasts')
        .update({
          ...podcastUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Buscar a categoria
      const { data: categoryData, error: categoryError } = await supabase
        .from('categories')
        .select('*')
        .eq('id', updatedPodcast.category_id)
        .single();

      if (categoryError) throw categoryError;

      // Montar o objeto final
      const finalPodcast = {
        ...updatedPodcast,
        category: categoryData,
        tags: podcast.tags || []
      };

      return finalPodcast as PodcastWithRelations;
    } catch (err: any) {
      console.error('Erro ao atualizar podcast:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deletePodcast = async (id: string) => {
    setLoading(true);
    setError(null);
    console.log('Deleting podcast:', id);

    try {
      const { error } = await supabase
        .from('podcasts') 
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        console.error('Error deleting podcast:', error);
        throw error;
      }

      // Update local state after successful deletion
      setPodcasts(prev => prev.filter(p => p.id !== id));
      console.log('Podcast deleted successfully');

    } catch (err: any) {
      console.error('Error deleting podcast:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePodcastTags = async (podcastId: string, tagIds: string[]) => {
    setLoading(true);
    setError(null);

    try {
      // First delete existing tags
      const { error: deleteError } = await supabase
        .from('podcast_tags')
        .delete()
        .eq('podcast_id', podcastId);

      if (deleteError) throw deleteError;

      // Then insert new tags if there are any
      if (tagIds.length > 0) {
        const { error: insertError } = await supabase
          .from('podcast_tags')
          .insert(
            tagIds.map(tagId => ({
              podcast_id: podcastId,
              tag_id: tagId
            }))
          );
        if (insertError) {
          console.error('Error inserting podcast tags:', insertError);
          setError(insertError.message);
          throw new Error(insertError.message);
        }
      }
      setLoading(false);
      return { error: null }; // Adicionando retorno para indicar sucesso
    } catch (err) {
      console.error('Error updating podcast tags:', err);
      setError(err.message);
      throw err;
    }
  };

  const fetchPodcastById = async (id: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/podcasts/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch podcast');
      }
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error fetching podcast:', err);
      setError(err.message);
      return null; // Retornando null em caso de erro
    } finally {
      setLoading(false);
    }
  };

  const incrementPlays = async (id: string) => {
    try {
      const { data: podcast, error: fetchError } = await supabase
        .from('podcasts')
        .select('plays')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      const currentPlays = podcast?.plays || 0;
      const { error: updateError } = await supabase
        .from('podcasts')
        .update({ plays: currentPlays + 1 })
        .eq('id', id);

      if (updateError) throw updateError;
    } catch (error) {
      console.error('Error incrementing plays:', error);
      throw error;
    }
  };

  return {
    loading,
    error,
    podcasts,
    setPodcasts,
    createPodcast,
    updatePodcast,
    deletePodcast,
    updatePodcastTags,
    fetchPodcasts
  };
}

export type { PodcastWithRelations };
export default usePodcasts;

export { usePodcasts }